import { Routes } from "@angular/router";
import { AuthGuard } from "@core/guards/auth.guard";
import { OnlineGuard } from "@core/guards/online.guard";
import { OnlineOrDownloadedGuard } from "@core/guards/online-or-downloaded.guard";
import { GuestGuard } from "@core/guards/guest.guard";
import { PhoneNumberGuard } from "@core/guards/phone-number.guard";
import { UserDataGuard } from "@core/guards/user-data.guard";
import { StartCourseTermsConditionsGuard } from "@core/guards/start-course-terms-conditions.guard";
import { EndCourseTermsConditionsGuard } from "@core/guards/end-course-terms-conditions.guard";
import { CoursePrerequisiteAccessGuard } from "@core/guards/course-prerequisite-access.guard";
import { MaintenanceGuard } from "@core/guards/maintenance.guard";
import { MaintenanceModeGuard } from "@core/guards/maintenance-mode.guard";
import { DefaultHomeRedirectGuard } from "@core/guards/default-home-redirect.guard";
import { InductionFeatureGuard } from "@core/guards/induction-feature.guard";
import {
  CatalogueAssignmentGuard,
  LearningPathsAssignmentGuard,
} from "@core/guards/user-assignment.guard";
import { CatalogueTranslationKeyEnum } from "@core/enums/catalogue-translation-key.enum";
import { InductionTranslationKeyEnum } from "@core/enums/induction-translation-key.enum";
import { CertificatesTranslationKeyEnum } from "@core/enums/certificates-translation-key.enum";
import { CustomTitleService } from "@shared/services/custom-title.service";
import { DefaultLayoutComponent } from "@core/layouts/default-layout/default-layout.component";
import { HeaderLayoutComponent } from "@core/layouts/header-layout/header-layout.component";
import { MinimalLayoutComponent } from "@core/layouts/minimal-layout/minimal-layout.component";
import { AuthLayoutComponent } from "@core/layouts/auth-layout/auth-layout.component";


const sessionProfileGuards: (typeof UserDataGuard | typeof PhoneNumberGuard)[] = [
  UserDataGuard,
  PhoneNumberGuard,
];

export const appRoutes: Routes = [
  {
    path: "maintenance-mode",
    loadChildren: () =>
      import("@modules/maintenance-mode/maintenance-mode.routes").then(
        (m) => m.maintenanceModeRoutes
      ),
    canActivate: [MaintenanceModeGuard],
  },

  {
    path: "redirect/:rt",
    loadChildren: () =>
      import("@shared/components/utility/redirect/redirect.routes").then(
        (m) => m.redirectRoutes
      ),
    canActivate: [MaintenanceGuard],
  },

  {
    path: "",
    pathMatch: "full",
    canActivate: [DefaultHomeRedirectGuard],
    children: [],
  },

  // Layout de autenticación (presentation + top-logo + detect-language-change)
  {
    path: "",
    component: AuthLayoutComponent,
    canActivate: [MaintenanceGuard],
    children: [
      {
        path: "login",
        canActivate: [GuestGuard],
        loadChildren: () =>
          import("@modules/login/login.routes").then((m) => m.loginRoutes),
        data: { animation: "login", title: "Iniciar Sesion" },
        resolve: {
          setTitle: CustomTitleService,
        },
      },
      {
        path: "login-password",
        canActivate: [GuestGuard],
        loadChildren: () =>
          import("@modules/login-password/login-password.routes").then(
            (m) => m.loginPasswordRoutes
          ),
        data: { animation: "login-password", title: "Iniciar Sesión con Contraseña" },
        resolve: {
          setTitle: CustomTitleService,
        },
      },
      {
        path: "otp-verification",
        loadChildren: () =>
          import("@modules/otp-verification/otp-verification.routes").then(
            (m) => m.otpVerificationRoutes
          ),
        data: { title: "otp-verification" },
        resolve: {
          setTitle: CustomTitleService,
        },
      },
      {
        path: "reset-password/:method",
        loadChildren: () =>
          import("@modules/reset-password/reset-password.routes").then(
            (m) => m.resetPasswordRoutes
          ),
      },
    ],
  },

  {
    path: "main",
    canActivate: [MaintenanceGuard, AuthGuard],
    loadComponent: () =>
      import("@modules/main/pages/main-page/main-page.component").then(
        (m) => m.MainPageComponent
      ),
  },

  // Layout mínimo (sin navbar ni header, solo animación)
  {
    path: "",
    component: MinimalLayoutComponent,
    canActivate: [MaintenanceGuard, AuthGuard],
    children: [
      {
        path: "",
        canActivate: sessionProfileGuards,
        children: [
          {
            path: "stack-questions/:courseId/:lessonId",
            canActivate: [OnlineGuard, StartCourseTermsConditionsGuard],
            loadChildren: () =>
              import("@modules/stack-questions/stack-questions.routes").then(
                (m) => m.stackQuestionsRoutes
              ),
            data: { animation: "stack-questions" },
          },
          {
            path: "lesson-survey/:courseId/lesson/:lessonId",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/lesson-survey/lesson-survey.routes").then(
                (m) => m.lessonSurveyRoutes
              ),
            data: { animation: "stack-questions", title: "stacked-questions" },
            resolve: {
              setTitle: CustomTitleService,
            },
          },
          {
            path: "course-quiz-result/:courseId/lesson/:lessonId",
            canActivate: [OnlineGuard, EndCourseTermsConditionsGuard],
            loadChildren: () =>
              import("@modules/course-quiz-result/course-quiz-result.routes").then(
                (m) => m.courseQuizResultRoutes
              ),
            data: { title: "courseQuizResult.pageTitle" },
            resolve: {
              setTitle: CustomTitleService,
            },
          },
          {
            path: "student-ranking/:id",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/student-ranking/student-ranking.routes").then(
                (m) => m.studentRankingRoutes
              ),
            data: { title: "student-ranking_tutor-participant_l25" },
            resolve: {
              setTitle: CustomTitleService,
            },
          },
        ],
      },
    ],
  },

  // Layout con navbar + header
  {
    path: "",
    component: DefaultLayoutComponent,
    canActivate: [MaintenanceGuard, AuthGuard],
    children: [
      {
        path: "",
        canActivate: sessionProfileGuards,
        children: [
          {
            path: "catalogue",
            canActivate: [CatalogueAssignmentGuard],
            loadChildren: () =>
              import("@modules/catalogue/catalogue.routes").then(
                (mod) => mod.catalogueRoutes
              ),
            data: {
              animation: "catalogue",
              title: "courseCatalogTitle",
              currentPage: "header.catalogue",
              headingKey: CatalogueTranslationKeyEnum.PageTitle,
              bell: true,
              isForumHelpOrCatalogue: true,
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "induction",
            canActivate: [InductionFeatureGuard],
            loadChildren: () =>
              import("@modules/induction/induction.routes").then((mod) => mod.inductionRoutes),
            data: {
              animation: "catalogue",
              title: InductionTranslationKeyEnum.DocumentTitle,
              currentPage: InductionTranslationKeyEnum.PageTitle,
              headingKey: InductionTranslationKeyEnum.PageTitle,
              bell: true,
              isForumHelpOrCatalogue: true,
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "learning-paths",
            canActivate: [LearningPathsAssignmentGuard],
            loadChildren: () =>
              import("@modules/learning-paths/learning-paths.routes").then(
                (mod) => mod.learningPathsRoutes
              ),
          },

          {
            path: "statistics",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/statistics/statistics.routes").then(
                (m) => m.statisticsRoutes
              ),
            data: { title: "navbar_l90", currentPage: "navbar.statistics", bell: true },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "help-forum",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/help-forum/help-forum.routes").then(
                (m) => m.helpForumRoutes
              ),
            data: {
              title: "help-forum_tutor-participant_l36",
              currentPage: "navbar_l59",
              headingKey: "navbar.helpForum",
              showBack: true,
              isForumHelpOrCatalogue: true,
              bell: true,
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "notifications",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/notifications/notifications.routes").then(
                (m) => m.notificationsRoutes
              ),
            data: {
              title: "notifications_l28",
              currentPage: "notifications_l28",
              headingKey: "notifications_l28",
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "press-releases",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/press-releases/press-releases.routes").then(
                (m) => m.pressReleasesMainRoutes
              ),
            data: {
              title: "Comunicados",
              currentPage: "navbar.pressReleases",
              headingKey: "pressReleasePage.title",
              bell: true,
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "settings",
            loadChildren: () =>
              import("@modules/settings/settings.routes").then(
                (m) => m.settingsRoutes
              ),
            data: {
              title: "settings.redesign.pageTitle",
              currentPage: "settings.redesign.pageTitle",
              headingKey: "settings.redesign.pageTitle",
              bell: true,
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },

          {
            path: "certificates",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/certificates/certificates.routes").then(
                (m) => m.certificatesRoutes
              ),
            data: {
              title: CertificatesTranslationKeyEnum.PageTitle,
              currentPage: CertificatesTranslationKeyEnum.MobileTitle,
              headingKey: CertificatesTranslationKeyEnum.PageTitle,
              bell: true,
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },
        ],
      },
    ],
  },

  // MinimalLayout para las sub-rutas del quiz de comunicados.
  // Las rutas hijas (question-preview, resolve-questions/:id, completed-questionnaire/:id)
  // son disjuntas respecto a pressReleasesMainRoutes (que solo tiene path:''),
  // por eso el router resuelve correctamente cada layout sin ambigüedad.
  {
    path: "",
    component: MinimalLayoutComponent,
    canActivate: [MaintenanceGuard, AuthGuard],
    children: [
      {
        path: "",
        canActivate: sessionProfileGuards,
        children: [
          {
            path: "press-releases",
            canActivate: [OnlineGuard],
            loadChildren: () =>
              import("@modules/press-releases/press-releases.routes").then(
                (m) => m.pressReleasesQuizRoutes
              ),
          },
        ],
      },
    ],
  },

  // Layout solo con header
  {
    path: "",
    component: HeaderLayoutComponent,
    canActivate: [MaintenanceGuard, AuthGuard],
    children: [
      {
        path: "",
        canActivate: sessionProfileGuards,
        children: [
          {
            path: "course-details/:courseId",
            canActivate: [OnlineOrDownloadedGuard, CoursePrerequisiteAccessGuard, EndCourseTermsConditionsGuard],
            loadChildren: () =>
              import("@modules/course-details/course-details.routes").then(
                (m) => m.courseDetailsRoutes
              ),
            data: {
              animation: "course-details",
              title: "course-details_l559",
              currentPage: "course-details_l559",
            },
            resolve: {
              setTitle: CustomTitleService,
            },
          },
        ],
      },
    ],
  },

  {
    path: "**",
    canActivate: [DefaultHomeRedirectGuard],
    children: [],
  },
];
